import { useCountByStatus } from "@/api/author/author";
import { readTablePreferences } from "@/hooks/useTablePreferences";
import { PagingParameters } from "@/api/dtos";
import {
  authorsColumns,
  authorsColumnsVisibility,
} from "@/config/dashboard/authors";
import { generateTable } from "@/config/dashboard/general";
import {
  useCountRelatedFields,
  useCreateRelatedFields,
  useDeleteRelation,
  useGetNotRelatedResource,
  useGetRelatedFields,
} from "@/api/related-fields";
import { Author } from "@/lib/types";
import { ColumnDef, VisibilityState } from "@tanstack/react-table";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { RelatedRecordRowActions } from "@/components/table/RelatedRecordRowActions";
import { createActionsColumnDef } from "@/components/table/table-actions-column";
import ShowElements from "../drawer/ShowElements";
import { LinkableType } from "../LinksManagement";
import { DataTable } from "../ui/datatable";
import { Input } from "../ui/input";
import { RelatedLinksListGridLayout } from "./RelatedLinksListGridLayout";
import { GridCardWithActions } from "./GridCardWithActions";
import { AuthorLinkGridCard } from "./relatedLinkGridCards";

interface LinksProps {
  id: string;
  type: LinkableType;
}

/**
 * AuthorLinks component
 * @returns {JSX.Element}
 */
const AuthorLinks = ({ id, type }: LinksProps) => {
  const { t } = useTranslation();

  const [getPaginationRelatedAuthors, setPaginationRelatedAuthors] =
    useState<PagingParameters>({
      page: 0,
      pageSize: 10,
    });

  const { data: relatedFields } = useGetRelatedFields(
    id,
    type,
    "relatedAuthor",
    getPaginationRelatedAuthors.pageSize,
    getPaginationRelatedAuthors.page * getPaginationRelatedAuthors.pageSize,
  );

  const { data: countRelatedAuthors } = useCountRelatedFields(id, type, [
    "relatedAuthor",
  ]);

  const { mutate: createRelatedFields } = useCreateRelatedFields(
    id,
    type,
    "relatedAuthor",
  );
  const { mutate: deleteRelation } = useDeleteRelation(
    id,
    type,
    "relatedAuthor",
  );

  const [searchContent, setSearchContent] = useState<string>("");

  const handleAddMode = () => {
    setIsAddMode(!isAddMode);
  };

  const [isAddMode, setIsAddMode] = useState(false);
  const [selectedAuthorId, setSelectedAuthorId] = useState<string | null>(null);

  const [getPagination, setPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });

  const { data: countByStatus } = useCountByStatus();
  const totalSize = countByStatus?.published + countByStatus?.draft;

  const { data: notRelatedAuthors } = useGetNotRelatedResource<Author>({
    resultType: "author",
    resourceId: id,
    pagination: getPagination,
    searchQuery: searchContent,
  });

  const handleCreateRelation = useCallback(
    (idToLink: string) => {
      createRelatedFields([id, idToLink]);
    },
    [createRelatedFields, id],
  );

  const handleDeleteRelation = useCallback(
    (photographyId: string) => {
      deleteRelation(photographyId);
    },
    [deleteRelation],
  );

  const baseColumns = useMemo(
    () => generateTable(authorsColumns, "author", t),
    [t],
  );

  const renderAuthorRowActions = useCallback(
    (item: Author) => (
      <RelatedRecordRowActions
          isAddMode={isAddMode}
          resourceId={item.id}
          previewType="author"
          previewElements={[
            {
              label: t("artistesAuteursDescriptionPrenom"),
              value: item.artistesAuteursDescriptionPrenom,
              type: "text",
            },
            {
              label: t("artistesAuteursDescriptionNom"),
              value: item.artistesAuteursDescriptionNom,
              type: "text",
            },
            {
              label: t("artistesAuteursDescriptionResumeDates"),
              value: item.artistesAuteursDescriptionResumeDates,
              type: "text",
            },
          ]}
          onDelete={() => handleDeleteRelation(item.id)}
          onAdd={() => handleCreateRelation(item.id)}
        />
    ),
    [handleCreateRelation, handleDeleteRelation, isAddMode, t],
  );

  const columns: ColumnDef<Author, any>[] = useMemo(() => {
    return [
      ...baseColumns,
      createActionsColumnDef<Author>(({ row }) =>
        renderAuthorRowActions(row.original),
      ),
    ];
  }, [baseColumns, renderAuthorRowActions]);

  const STORAGE_KEY = "author-links-table";
  const defaultVisibility = { ...authorsColumnsVisibility, actions: true };
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    () => {
      try {
        const saved = readTablePreferences(STORAGE_KEY);
        if (saved) {
          const { visibility } = saved;
          if (visibility && typeof visibility === "object")
            return { ...defaultVisibility, ...visibility };
        }
      } catch {
        // ignore
      }
      return { ...defaultVisibility };
    },
  );
  const [columnsOrder, setColumnsOrder] = useState<string[]>(() => {
    try {
      const saved = readTablePreferences(STORAGE_KEY);
      if (saved) {
        const { order, columnOrder } = saved;
        const storedOrder = Array.isArray(order)
          ? order
          : Array.isArray(columnOrder)
            ? columnOrder
            : null;
        if (Array.isArray(storedOrder)) {
          const normalizedOrder = [...new Set(storedOrder)] as string[];
          return normalizedOrder.includes("actions")
            ? [...normalizedOrder.filter((id) => id !== "actions"), "actions"]
            : normalizedOrder;
        }
      }
    } catch {
      // ignore
    }
    return [];
  });

  const relatedPaginationProps = {
    pagination: getPaginationRelatedAuthors,
    setPage: setPaginationRelatedAuthors,
    totalSize: countRelatedAuthors?.relatedAuthor,
  };
  const addModeTable = (
    <DataTable
      columns={columns}
      data={notRelatedAuthors?.data ?? []}
      pagination={{
        pagination: getPagination,
        setPage: setPagination,
        totalSize: totalSize,
      }}
      storageKey={STORAGE_KEY}
      options={{
        enableColumnResizing: true,
        onColumnVisibilityChange: setColumnVisibility,
        onColumnOrderChange: setColumnsOrder,
        state: { columnVisibility, columnOrder: columnsOrder },
      }}
    />
  );
  const listTable = (
    <DataTable
      columns={columns}
      data={relatedFields ?? []}
      pagination={relatedPaginationProps}
      storageKey={STORAGE_KEY}
      options={{
        enableColumnResizing: true,
        onColumnVisibilityChange: setColumnVisibility,
        onColumnOrderChange: setColumnsOrder,
        state: { columnVisibility, columnOrder: columnsOrder },
      }}
    />
  );

  return (
    <div className="w-full">
      <RelatedLinksListGridLayout
        storageKey={STORAGE_KEY}
        title={t("linkedAuthors")}
        backLabel={t("linkedEntries")}
        isAddMode={isAddMode}
        onBack={() => setIsAddMode(false)}
        onToggleAdd={handleAddMode}
        searchInput={
          <div className="flex items-center gap-2">
            <Input
              placeholder={t("search")}
              className="w-80"
              value={searchContent}
              onChange={(e) => setSearchContent(e.target.value)}
            />
          </div>
        }
        addModeTable={addModeTable}
        listTable={listTable}
        gridContent={
          <>
            {(relatedFields ?? []).map((item: Author) => (
              <div key={item.id}>
                <GridCardWithActions actions={renderAuthorRowActions(item)}>
                <AuthorLinkGridCard
                  item={item}
                  t={t}
                  onTextClick={() => setSelectedAuthorId(item.id)}
                />
              </GridCardWithActions>
              <ShowElements
                  type="author"
                  id={item.id}
                  open={selectedAuthorId === item.id}
                  onOpenChange={(open) => {
                    if (!open) {
                      setSelectedAuthorId(null);
                    }
                  }}
                  trigger={<span />}
                  elements={[
                    {
                      label: t("artistesAuteursDescriptionPrenom"),
                      value: item.artistesAuteursDescriptionPrenom,
                      type: "text",
                    },
                    {
                      label: t("artistesAuteursDescriptionNom"),
                      value: item.artistesAuteursDescriptionNom,
                      type: "text",
                    },
                    {
                      label: t("artistesAuteursDescriptionResumeDates"),
                      value: item.artistesAuteursDescriptionResumeDates,
                      type: "text",
                    },
                  ]}
                />
              </div>
            ))}
          </>
        }
        relatedPagination={relatedPaginationProps}
      />
    </div>
  );
};

export default AuthorLinks;
